import { useState, useEffect } from 'react';
import type { ProjectFile, RenderedVideo, EditPlan, PlanMessage, PlanQuestion, FinishingTouches } from '@/types/video-enhancer';
import { resolveVideoUrl, resolveOutputConfig } from '@/lib/utils';
import { buildPlanAssets } from '@/lib/enhancerMeta';

interface UsePlanModeParams {
  files: ProjectFile[];
  selectedFiles: Set<string>; // NEW: Selected file IDs for AI Creative mode
  analysisResults: Record<string, any>;
  scriptContent: string | null;
  currentProject: string;
  aspectRatio: string;
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
  onRenderCreated: (video: RenderedVideo) => void;
  onRenderUpdated: (videoId: string, updates: Partial<RenderedVideo>) => void;
  pollRenderStatus: (videoId: string, renderId: string) => void;
}

export function usePlanMode({
  files,
  selectedFiles,
  analysisResults,
  scriptContent,
  currentProject,
  aspectRatio,
  toast,
  onRenderCreated,
  onRenderUpdated,
  pollRenderStatus,
}: UsePlanModeParams) {
  const [planModeActive, setPlanModeActive] = useState(false);
  const [planConversation, setPlanConversation] = useState<PlanMessage[]>([]);
  const [isPlanProcessing, setIsPlanProcessing] = useState(false);
  const [editPlan, setEditPlan] = useState<EditPlan | null>(null);
  const [isPlanApproved, setIsPlanApproved] = useState(false);
  const [isPlanRendering, setIsPlanRendering] = useState(false);
  const [planQuestions, setPlanQuestions] = useState<PlanQuestion[]>([]);
  const [finishingTouches, setFinishingTouches] = useState<FinishingTouches>({});

  // Sync finishing touches from editPlan when AI returns them
  useEffect(() => {
    if (editPlan?.finishingTouches) {
      setFinishingTouches(editPlan.finishingTouches);
    }
  }, [editPlan]);

  // Consolidated in enhancerMeta (tolerates both start/end and start_time/end_time segments)
  const buildAssetsMetadataForPlan = () => buildPlanAssets(files, selectedFiles, analysisResults);

  const handleStartPlanMode = async () => {
    // Check if any files are selected
    if (selectedFiles.size === 0) {
      toast({
        title: 'No clips selected',
        description: 'Please select clips from your bin before starting AI Creative mode',
        variant: 'destructive',
      });
      return;
    }

    const hasExistingConversation = planConversation.length > 0;

    setPlanModeActive(true);
    setIsPlanApproved(false);
    setIsPlanRendering(false);

    if (hasExistingConversation) {
      // If there's already a completed plan, just reopen the panel — don't clear anything
      if (editPlan) {
        return;
      }
      // No plan yet, just an ongoing conversation — resume it
      setPlanQuestions([]);
      return;
    }

    setIsPlanProcessing(true);
    setPlanConversation([]);
    setEditPlan(null);
    setPlanQuestions([]);

    try {
      const assets_metadata = buildAssetsMetadataForPlan();

      const response = await fetch('/api/script/plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assets_metadata,
          script_text: scriptContent || null,
          is_start: true,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start plan conversation');
      }

      const data = await response.json();

      if (data.aiResponse) {
        setPlanConversation([{
          role: 'ai',
          content: data.aiResponse,
          timestamp: Date.now(),
        }]);
      }

      if (data.questions && data.questions.length > 0) {
        setPlanQuestions(data.questions);
      }

      if (data.isComplete && data.editPlan) {
        setEditPlan(data.editPlan);
      }

    } catch (error) {
      console.error('Failed to start plan mode:', error);
      toast({
        title: 'Plan mode failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      setPlanModeActive(false);
    } finally {
      setIsPlanProcessing(false);
    }
  };

  const handlePlanMessage = async (message: string) => {
    // Build updated conversation BEFORE the setState so we can send it to the API
    // (planConversation from the closure is stale after setPlanConversation)
    const updatedConversation = [...planConversation, {
      role: 'user' as const,
      content: message,
      timestamp: Date.now(),
    }];
    setPlanConversation(updatedConversation);

    setIsPlanProcessing(true);
    setPlanQuestions([]);

    try {
      const assets_metadata = buildAssetsMetadataForPlan();

      const response = await fetch('/api/script/plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assets_metadata,
          conversation_history: updatedConversation,
          user_message: message,
          script_text: scriptContent || null,
          current_plan: editPlan || null,  // Pass current plan for modifications
          is_start: false,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to continue conversation');
      }

      const data = await response.json();

      if (data.aiResponse) {
        setPlanConversation(prev => [...prev, {
          role: 'ai',
          content: data.aiResponse,
          timestamp: Date.now(),
        }]);
      }

      if (data.questions && data.questions.length > 0) {
        setPlanQuestions(data.questions);
      }

      if (data.isComplete && data.editPlan) {
        setEditPlan(data.editPlan);
      }

    } catch (error) {
      console.error('Failed to send plan message:', error);
      toast({
        title: 'Message failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      setPlanConversation(prev => [...prev, {
        role: 'ai',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: Date.now(),
      }]);
    } finally {
      setIsPlanProcessing(false);
    }
  };

  const handlePlanQuestionSelect = (questionId: string, option: string) => {
    handlePlanMessage(option);
  };

  const handleApprovePlan = async (modifiedClips?: any[]) => {
    if (!editPlan) {
      toast({
        title: 'No plan to approve',
        description: 'Complete the planning conversation first',
        variant: 'destructive',
      });
      return;
    }

    setIsPlanRendering(true);
    setIsPlanApproved(true);

    // Declared outside the try so the catch can safely reference it even when
    // the failure happens before the queued entry is created.
    let queuedVideo: RenderedVideo | null = null;

    try {
      const assetsMetadata: any[] = [];
      // Use only selected files for rendering
      let selectedFilesList = files.filter(f => selectedFiles.has(f.id));

      // If user modified clips, filter and reorder based on their changes
      if (modifiedClips && modifiedClips.length > 0) {
        const modifiedAssetNames = modifiedClips.map(c => c.assetName);
        // Filter to only include clips that weren't removed
        const filteredByName = selectedFilesList.filter(f => modifiedAssetNames.includes(f.name));

        // Fallback: if name matching produces empty results (LLM generated different names),
        // use all selected files instead of an empty list
        if (filteredByName.length > 0) {
          selectedFilesList = filteredByName;
          // Reorder files to match the user's desired clip order
          selectedFilesList.sort((a, b) => {
            const aIndex = modifiedAssetNames.indexOf(a.name);
            const bIndex = modifiedAssetNames.indexOf(b.name);
            return aIndex - bIndex;
          });
        } else {
          console.warn('⚠️ Modified clip names did not match any files, using all selected files');
          console.warn('   Modified names:', modifiedAssetNames);
          console.warn('   File names:', selectedFilesList.map(f => f.name));
        }
      }

      const videoFiles = selectedFilesList.filter(f => f.fileType === 'video');
      const audioFiles = selectedFilesList.filter(f => f.fileType === 'audio');
      const imageFiles = selectedFilesList.filter(f => f.fileType === 'image');

      videoFiles.forEach((file, index) => {
        let videoUrl = resolveVideoUrl(file.s3Url, file.s3Key);
        if (!videoUrl) return;

        // Convert relative /api/s3/ URLs to full S3 URLs for Lambda
        if (videoUrl.startsWith('/api/s3/')) {
          const s3Path = videoUrl.replace('/api/s3/', '');
          videoUrl = `https://hey-joe-videos.s3.amazonaws.com/${s3Path}`;
        }

        // For rendering, we DON'T include transcripts - they're already in the plan
        // Only include talent info if available
        let comment = '';
        if ((file as any).talent && (file as any).talent.length > 0) {
          comment = `Talent: ${(file as any).talent.join(', ')}`; }

        const rawDuration = file.duration || 5;
        const minutes = Math.floor(rawDuration / 60);
        const seconds = Math.round(rawDuration % 60);
        const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        assetsMetadata.push({
          url: videoUrl,
          name: file.name || `clip_${index + 1}`,
          duration: formattedDuration,
          ...(comment ? { comment } : {}),
        });
      });

      audioFiles.forEach((file, index) => {
        let audioUrl = file.s3Url;
        if (!audioUrl) return;
        if (audioUrl.startsWith('/api/s3/')) {
          const s3Path = audioUrl.replace('/api/s3/', '');
          audioUrl = `https://hey-joe-videos.s3.amazonaws.com/${s3Path}`;
        }
        const rawDuration = file.duration || 5;
        const minutes = Math.floor(rawDuration / 60);
        const seconds = Math.round(rawDuration % 60);
        assetsMetadata.push({
          url: audioUrl,
          name: file.name || `audio_${index + 1}`,
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
        });
      });

      imageFiles.forEach((file, index) => {
        let imageUrl = file.s3Url;
        if (!imageUrl) return;

        // Convert relative /api/s3/ URLs to full S3 URLs for Lambda
        if (imageUrl.startsWith('/api/s3/')) {
          const s3Path = imageUrl.replace('/api/s3/', '');
          imageUrl = `https://hey-joe-videos.s3.amazonaws.com/${s3Path}`;
        }

        assetsMetadata.push({
          url: imageUrl,
          name: file.name || `image_${index + 1}`,
          width: String((file as any).width || 1920),
          height: String((file as any).height || 1080),
        });
      });

      const hasMusicAssets = audioFiles.length > 0;
      const musicSection = hasMusicAssets && editPlan.musicDirection && editPlan.musicDirection.toLowerCase() !== 'none'
        ? `Music: Use the provided audio track(s) - ${editPlan.musicDirection}`
        : '';

      // Use modified clips if provided, otherwise use original plan
      const clipsToUse = modifiedClips && modifiedClips.length > 0 ? modifiedClips : editPlan.clipPriorities;

      // Build finishing touches instructions for the Shotstack JSON generator
      const filterInstruction = finishingTouches.filter
        ? `\nVISUAL FILTER: Apply a "${finishingTouches.filter}" filter to all video clips. Use the Shotstack filter property on each video clip: "filter": { "type": "${finishingTouches.filter}" }.`
        : '';

      // Captions are injected by the backend, not the AI — no caption instruction needed here

      const planPrompt = `
=== AI CREATIVE EDIT PLAN ===
Summary: ${editPlan.summary}

Style: ${editPlan.style}
Tone: ${editPlan.tone}
Pacing: ${editPlan.pacing}
Target Duration: ~${editPlan.suggestedDuration} seconds

${editPlan.keyMoments?.length ? `Key Moments to include:\n${editPlan.keyMoments.map((m, i) => `${i + 1}. ${m}`).join('\n')}` : ''}

${editPlan.focusAreas?.length ? `Focus Areas: ${editPlan.focusAreas.join(', ')}` : ''}

${musicSection}
${editPlan.transitionStyle ? `Transition Style: ${editPlan.transitionStyle}` : ''}
${editPlan.colorGrade ? `Color Grade: ${editPlan.colorGrade}` : ''}

${editPlan.textOverlays?.length ? `Text Overlays:\n${editPlan.textOverlays.map(t => `- "${t}"`).join('\n')}` : ''}

${clipsToUse?.length ? `Clip Usage (in order):\n${clipsToUse.map((c, idx) => `${idx + 1}. ${c.assetName} (${c.priority}): ${c.reason}${c.suggestedTrim ? ` [${c.suggestedTrim}]` : ''}`).join('\n')}` : ''}
${filterInstruction}
${scriptContent ? `\n--- SCRIPT ---\n${scriptContent}\n---` : ''}
=== END PLAN ===

IMPORTANT: Only use assets that are provided in the assets_metadata. Do NOT suggest or reference any assets that are not uploaded.
Create the edit according to this plan. Use intelligent clip selection and trim points based on the transcripts.
      `.trim();

      const firstThumbnail = videoFiles[0]?.thumbnailUrl;
      queuedVideo = {
        id: Math.random().toString(36).substr(2, 9),
        name: `PlanEdit_${new Date().toISOString().split('T')[0]}_${Date.now()}`,
        thumbnailUrl: firstThumbnail,
        timestamp: new Date().toISOString(),
        duration: editPlan.suggestedDuration || 30,
        status: 'processing',
        progress: 0,
        configuration: {
          clips: [],
          masterPrompt: planPrompt,
        },
        // Save AI Creative plan and conversation for history
        aiCreativePlan: editPlan,
        aiCreativeConversation: planConversation,
        // Save finishing touches for post-render pipeline
        finishingTouches: finishingTouches,
        pipelineStage: (finishingTouches.enhanceAudio) ? 'rendering' : undefined,
      };

      onRenderCreated(queuedVideo);

      toast({
        title: 'Generating edit',
        description: 'Creating your video based on the approved plan...',
      });

      // Resolve output dimensions (handles "source" + standard ratios)
      const outputConfig = resolveOutputConfig(aspectRatio, videoFiles);
      const effectiveAspectRatio = aspectRatio === 'source'
        ? `${outputConfig.output_width}:${outputConfig.output_height}`
        : aspectRatio;

      const previewResponse = await fetch('/api/shotstack/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: currentProject,
          users_text: planPrompt,
          users_objective: `Create an ${editPlan.style} video with ${editPlan.tone} tone in ${effectiveAspectRatio} aspect ratio`,
          assets_metadata: assetsMetadata,
          aspect_ratio: effectiveAspectRatio,
          shotstack_resolution: outputConfig.shotstack_resolution || 'hd',
          output_width: outputConfig.output_width,
          output_height: outputConfig.output_height,
        }),
      });

      if (!previewResponse.ok) {
        const errorData = await previewResponse.json();
        throw new Error(errorData.details || errorData.error || 'Failed to generate edit');
      }

      const previewData = await previewResponse.json();
      if (!previewData.success || !previewData.shotstackJson) {
        throw new Error('Invalid response from AI generator');
      }

      // Remove debug_logs before sending to Shotstack (can be 450KB+)
      const shotstackJsonForRender = { ...previewData.shotstackJson };
      delete shotstackJsonForRender.debug_logs;

      // Build caption injection data if captions are enabled
      let captionData: any = undefined;
      console.log('🎬 Caption check - finishingTouches.addCaptions:', finishingTouches.addCaptions);
      console.log('🎬 Caption check - finishingTouches:', finishingTouches);
      if (finishingTouches.addCaptions) {
        const captionAssets: Array<{ url: string; words: any[] }> = [];
        // Use ALL files, not just selected ones (AI mode uses all uploaded files)
        const filesToUse = files.filter(f => f.fileType === 'video');
        console.log('🎬 Caption: Found', filesToUse.length, 'video files for captions');
        for (const file of filesToUse) {
          const words = file.analysisData?.transcript?.words
            || file.analysisData?.transcript?.segments
            || [];
          console.log(`🎬 Caption: File "${file.name}" has ${words.length} words/segments`);
          if (words.length === 0) continue;
          let videoUrl = resolveVideoUrl(file.s3Url, file.s3Key);
          if (videoUrl && videoUrl.startsWith('/api/s3/')) {
            videoUrl = `https://hey-joe-videos.s3.amazonaws.com/${videoUrl.replace('/api/s3/', '')}`;
          }
          console.log(`🎬 Caption: File "${file.name}" URL: ${videoUrl}`);
          if (videoUrl) {
            captionAssets.push({ url: videoUrl, words });
          }
        }
        console.log('🎬 Caption: Total caption assets:', captionAssets.length);
        if (captionAssets.length > 0) {
          captionData = {
            enabled: true,
            style: finishingTouches.captionStyle || 'default',
            assets: captionAssets,
          };
          console.log('🎬 Caption: Built captionData:', JSON.stringify(captionData, null, 2));
        }
      }

      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      const renderResponse = await fetch('/api/shotstack/render', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          json: shotstackJsonForRender,
          ...(captionData ? { captionData } : {}),
          ...(finishingTouches.filter ? { filterData: { type: finishingTouches.filter } } : {}),
          outputSize: { width: outputConfig.output_width, height: outputConfig.output_height },
        }),
      });

      const renderData = await renderResponse.json();

      if (!renderResponse.ok || !renderData.success) {
        throw new Error(renderData.error || 'Failed to submit render');
      }

      onRenderUpdated(queuedVideo.id, {
        status: 'rendering',
        renderId: renderData.id,
        shotstackJson: previewData.shotstackJson,
      });

      pollRenderStatus(queuedVideo.id, renderData.id);

      toast({
        title: 'Render started',
        description: 'Your video is now rendering. Check the Rendered Videos section for progress.',
      });

      // Keep plan mode open so user can iterate — add confirmation to conversation
      setPlanConversation(prev => [...prev, {
        role: 'ai' as const,
        content: 'Render submitted! Your video is being generated. You can adjust the clip order, trim points, or finishing touches and render again, or use "Refine Plan" to make bigger changes.',
        timestamp: Date.now(),
      }]);
      // Reset approved state so user can re-render with tweaks
      setIsPlanApproved(false);

    } catch (error: any) {
      console.error('Plan render error:', error);
      if (queuedVideo) {
        onRenderUpdated(queuedVideo.id, {
          status: 'failed',
          error: error.message || 'Failed to render video from plan',
        });
      }
      toast({
        title: 'Render failed',
        description: error.message || 'Failed to render video from plan',
        variant: 'destructive',
      });
    } finally {
      setIsPlanRendering(false);
    }
  };

  const handleClosePlanMode = () => {
    setPlanModeActive(false);
  };

  const handleRefinePlan = () => {
    setEditPlan(null);
    setPlanQuestions([]);
    setPlanConversation(prev => [...prev, {
      role: 'ai' as const,
      content: "No problem! What would you like to change about the plan? You can describe adjustments to the style, pacing, clip selection, or any other aspect.",
      timestamp: Date.now(),
    }]);
  };

  // Load a plan from a previously rendered video so the user can iterate
  const loadPlanFromRender = (video: RenderedVideo) => {
    if (!video.aiCreativePlan) return;

    setPlanModeActive(true);
    setEditPlan(video.aiCreativePlan);
    setPlanConversation([
      // Restore previous conversation if available
      ...(video.aiCreativeConversation || []),
      {
        role: 'ai' as const,
        content: `Loaded the plan from "${video.name}". You can adjust the clip order, trim points, or finishing touches and render again, or describe changes you'd like to make.`,
        timestamp: Date.now(),
      },
    ]);
    setPlanQuestions([]);
    setIsPlanApproved(false);
    setIsPlanRendering(false);
    setIsPlanProcessing(false);
    if (video.finishingTouches) {
      setFinishingTouches(video.finishingTouches);
    }
  };

  // Reset plan state (e.g., on project switch)
  const resetPlanState = () => {
    setPlanModeActive(false);
    setPlanConversation([]);
    setIsPlanProcessing(false);
    setEditPlan(null);
    setIsPlanApproved(false);
    setIsPlanRendering(false);
    setPlanQuestions([]);
    setFinishingTouches({});
  };

  return {
    planModeActive,
    setPlanModeActive,
    planConversation,
    isPlanProcessing,
    editPlan,
    isPlanApproved,
    isPlanRendering,
    planQuestions,
    finishingTouches,
    setFinishingTouches,
    handleStartPlanMode,
    handlePlanMessage,
    handlePlanQuestionSelect,
    handleApprovePlan,
    handleClosePlanMode,
    handleRefinePlan,
    loadPlanFromRender,
    resetPlanState,
  };
}
